import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { createStore } from '@ngneat/elf';
import { selectAllEntities, selectEntity, setEntities, updateEntities, upsertEntities, withEntities } from '@ngneat/elf-entities';
import { tap, first, switchMap, shareReplay } from 'rxjs';

import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { Models } from '../../models/global.models';
import { WithId } from './store.models';
import { REQUIRES_SECURITY_PROFILE } from '../../../interceptors';

const store = createStore({ name: 'leads' }, withEntities<WithId<Models.Lead, 'guid'>, 'guid'>({ idKey: 'guid' }));

/** Leads store (for grid) */
@Injectable({ providedIn: 'root' })
export class LeadsRepository {
  private readonly _http = inject(HttpClient);
  private readonly _environment = inject(APP_ENV);

  private _baseUrl = this._environment.retail.apiUrl;

  all$ = this.get().pipe(shareReplay(1));

  selectEntity(guid: string) {
    return store.pipe(selectEntity(guid));
  }

  reset() {
    store.reset();
  }

  get() {
    return this._http
      .get<WithId<Models.Lead, 'guid'>[]>(`${this._baseUrl}/leads/pipeline`, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) })
      .pipe(
        tap((leads) => {
          store.update(setEntities(leads));
        }),
        switchMap(() => store.pipe(selectAllEntities())),
      );
  }

  post(lead: Models.Lead) {
    return this._http
      .post<WithId<Models.Lead, 'guid'>>(`${this._baseUrl}/leads`, lead, { context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true) })
      .pipe(
        tap((response) => {
          store.update(upsertEntities(response));
        }),
      );
  }

  patch(lead: Models.Lead) {
    return this._http.patch(`${this._baseUrl}/leads/${lead.guid}`, lead).pipe(
      tap(() => {
        store.update(updateEntities(lead.guid, (entity) => ({ ...entity, ...lead })));
      }),
      switchMap(() => store.pipe(selectEntity(lead.guid))),
      first(),
    );
  }
}
